import React, { useState } from "react";
import useRouter from "../../../hooks/router";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

const NAV_BG   = "#1a3a4a";
const ACCENT   = "#00c9a7";
const ACCENT2  = "#f7c94b";
const DARK_BG  = "#111e27";
const CARD_BG  = "#1e3547";
const TEXT_MAIN = "#e8f0f4";
const TEXT_MUTED = "#8fafc4";
const SURFACE   = "rgba(255,255,255,0.05)";
const BORDER    = "rgba(255,255,255,0.12)";

const Nav = () => {
    const { handleNavigation } = useRouter();
    const [menuOpen, setMenuOpen] = useState(false);

    const navItems = [
        { label: "Pricing", path: "/pricing" },
        { label: "Blog", path: "/blog" },
        { label: "Sign In", path: "/login" },
        { label: "Get Started Free", path: "/signup" },
    ];

    return (
        <nav style={{ background: NAV_BG, borderBottom: `1px solid ${BORDER}` }} className="fixed top-0 left-0 w-full z-50 shadow-md">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
                <img
                    src="/logo.svg"
                    alt="RefAlign"
                    className="h-9 cursor-pointer"
                    onClick={() => handleNavigation("/")}
                />

                <div className="hidden md:flex items-center gap-3">
                    <button
                        onClick={() => handleNavigation("/pricing")}
                        style={{ color: TEXT_MAIN, border: `1px solid ${BORDER}`, background: SURFACE }}
                        className="px-5 py-2 rounded-lg text-sm font-medium hover:bg-white hover:bg-opacity-10 transition-all"
                    >
                        Pricing
                    </button>
                    <button
                        onClick={() => handleNavigation("/signup")}
                        style={{ background: ACCENT, color: "#0d2230" }}
                        className="px-5 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-all shadow-md"
                    >
                        Get Started Free
                    </button>
                </div>

                <button
                    className="md:hidden inline-flex items-center justify-center rounded-lg p-2"
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-label="Open menu"
                    style={{ color: TEXT_MAIN }}
                >
                    {menuOpen ? <CloseIcon sx={{ color: TEXT_MAIN }} /> : <MenuIcon sx={{ color: TEXT_MAIN }} />}
                </button>
            </div>

            {menuOpen && (
                <div className="md:hidden px-4 pb-4">
                    <div style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}` }} className="rounded-2xl p-3 flex flex-col gap-2">
                        {navItems.map((item) => (
                            <button
                                key={item.label}
                                onClick={() => { setMenuOpen(false); handleNavigation(item.path); }}
                                style={{ color: TEXT_MAIN, background: item.path === "/signup" ? ACCENT : "transparent" }}
                                className={`w-full text-left px-4 py-3 rounded-xl text-sm font-semibold transition-all ${item.path === "/signup" ? "text-[#0d2230]" : "hover:bg-white hover:bg-opacity-5"}`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </nav>
    );
};

const Hero = () => {
    const { handleNavigation } = useRouter();
    return (
        <section
            style={{ background: `linear-gradient(135deg, ${DARK_BG} 0%, #1a3a4a 100%)` }}
            className="pt-32 pb-24 px-4 sm:px-6"
        >
            <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
                <div className="flex-1 text-center lg:text-left w-full">
                    <div
                        style={{ background: "rgba(0,201,167,0.12)", color: ACCENT, border: "1px solid rgba(0,201,167,0.3)" }}
                        className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6"
                    >
                        🏟️ Built for sports organizations
                    </div>
                    <h1 style={{ color: TEXT_MAIN }} className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
                        Find games.<br/>
                        <span style={{ color: ACCENT }}>Run your referee program better.</span>
                    </h1>
                    <p style={{ color: TEXT_MUTED }} className="text-xl leading-relaxed mb-10 max-w-xl">
                        RefAlign helps referee organizations manage games, communicate faster, and keep everything moving.
                        If you’re a referee, find your assignments. If you assign games, keep everything in one place.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                        <button
                            onClick={() => handleNavigation("/signup")}
                            style={{ background: ACCENT, color: "#0d2230" }}
                            className="w-full sm:w-auto px-8 py-4 rounded-xl text-base font-bold hover:opacity-90 transition-all shadow-lg"
                        >
                            Get Started
                        </button>
                        <button
                            onClick={() => handleNavigation("/signup")}
                            style={{ color: TEXT_MAIN, border: `1px solid ${BORDER}`, background: SURFACE }}
                            className="w-full sm:w-auto px-8 py-4 rounded-xl text-base font-medium hover:bg-white hover:bg-opacity-5 transition-all"
                        >
                            Book a Demo
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 justify-center lg:justify-start max-w-2xl">
                        {[
                            { val: "Find games", label: "for referees" },
                            { val: "Assign faster", label: "without spreadsheet chaos" },
                            { val: "Get started", label: "free today" },
                        ].map((s) => (
                            <div key={s.label} className="text-center lg:text-left">
                                <div style={{ color: ACCENT2 }} className="text-xl font-bold leading-tight">{s.val}</div>
                                <div style={{ color: TEXT_MUTED }} className="text-sm">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex-1 flex justify-center w-full">
                    <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, boxShadow: "0 30px 80px rgba(0,0,0,0.38)" }} className="rounded-2xl overflow-hidden shadow-2xl w-full max-w-xl">
                        <div style={{ background: "#0d2230" }} className="flex items-center gap-2 px-4 py-3">
                            <div className="w-3 h-3 rounded-full bg-red-400 opacity-80"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-400 opacity-80"></div>
                            <div className="w-3 h-3 rounded-full bg-green-400 opacity-80"></div>
                            <div style={{ background: "rgba(255,255,255,0.07)", color: TEXT_MUTED }} className="ml-4 flex-1 text-xs px-3 py-1 rounded-md text-center">
                                app.refalign.com
                            </div>
                        </div>
                        <img src="/HomeScreenPhoto.png" alt="RefAlign dashboard screenshot" className="w-full" />
                    </div>
                </div>
            </div>
        </section>
    );
};

const features = [
    { icon: "📋", title: "Import & Assign Games", body: "Upload a spreadsheet of games and assign referees in seconds. Bulk assignment with smart conflict detection keeps your schedule clean." },
    { icon: "💳", title: "End-to-End Payments", body: "Handle payouts, 1099s, and registration forms automatically. Referees get paid on time, every time." },
    { icon: "📱", title: "Instant Notifications", body: "Referees receive game alerts via text and email the moment they're assigned. No more missed games." },
    { icon: "📅", title: "Referee Calendar", body: "Each referee sees a personal calendar of their upcoming games — location, co-officials, and pay rate all in one view." },
    { icon: "🔍", title: "Smart Filtering", body: "Filter referees by certification, availability, and location. Find the right official for every game instantly." },
    { icon: "📊", title: "Organization Dashboard", body: "Track games, payments, and referee performance across your entire organization from a single dashboard." },
];

const Features = () => (
    <section style={{ background: "#0f1e29" }} className="py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
                <h2 style={{ color: TEXT_MAIN }} className="text-4xl font-bold mb-4">
                    Everything your organization needs
                </h2>
                <p style={{ color: TEXT_MUTED }} className="text-lg max-w-2xl mx-auto">
                    RefAlign replaces the chaos of spreadsheets and group texts with a purpose-built platform
                    for referee management.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((f) => (
                    <div key={f.title} style={{ background: CARD_BG, border: "1px solid rgba(255,255,255,0.07)", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }} className="p-7 rounded-2xl hover:border-opacity-20 transition-all group">
                        <div className="text-3xl mb-4">{f.icon}</div>
                        <h3 style={{ color: TEXT_MAIN }} className="text-lg font-semibold mb-2">{f.title}</h3>
                        <p style={{ color: TEXT_MUTED }} className="text-sm leading-relaxed">{f.body}</p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const steps = [
    { num: "01", title: "Add your organization", body: "Sign up and create your organization profile in under 2 minutes. No credit card required." },
    { num: "02", title: "Import your games", body: "Upload a CSV of your game schedule or create games manually. RefAlign handles the rest." },
    { num: "03", title: "Assign referees", body: "Drag, drop, or bulk-assign officials to games. Referees are notified instantly." },
    { num: "04", title: "Track & pay", body: "Monitor attendance, collect reports, and process payments automatically at the end of the season." },
];

const HowItWorks = () => (
    <section style={{ background: "#111e27" }} className="py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
                <h2 style={{ color: TEXT_MAIN }} className="text-4xl font-bold mb-4">Up and running in minutes</h2>
                <p style={{ color: TEXT_MUTED }} className="text-lg">No training. No onboarding call. Just a modern tool that works.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {steps.map((s) => (
                    <div key={s.num} className="flex gap-5">
                        <div style={{ color: ACCENT, opacity: 0.4 }} className="text-5xl font-black leading-none pt-1 select-none">
                            {s.num}
                        </div>
                        <div>
                            <h3 style={{ color: TEXT_MAIN }} className="text-lg font-semibold mb-1">{s.title}</h3>
                            <p style={{ color: TEXT_MUTED }} className="text-sm leading-relaxed">{s.body}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const testimonials = [
    { name: "John Jussen", role: "Soccer Referee", img: "/JohnProfilePicture.png", quote: "Ever since my club started using RefAlign I haven't missed a game. The UI is clean and way better than anything else I've used.", rating: 5 },
    { name: "Mike Levene", role: "Referee Assigner", img: "/MikeProfilePicture.png", quote: "Before RefAlign we lived in spreadsheets and email. After switching, we cut assignment time from hours to under 20 minutes.", rating: 5 },
    { name: "Artsemi Kaval", role: "Soccer Referee", img: "/ArtProfilePicture.png", quote: "RefAlign is easy, intuitive, and reliable. I always know where I need to be and what I'm getting paid.", rating: 5 },
    { name: "Sarah Mitchell", role: "Club Administrator", img: "/ellipse-1@2x.png", quote: "Managing 80+ referees used to be a nightmare. RefAlign turned it into something I can handle in the morning before coffee.", rating: 5 },
];

const Stars = ({ n }) => (
    <div className="flex gap-0.5 mb-3">
        {Array.from({ length: n }).map((_, i) => <span key={i} style={{ color: ACCENT2 }}>★</span>)}
    </div>
);

const Testimonials = () => (
    <section style={{ background: "#0f1e29" }} className="py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
                <h2 style={{ color: TEXT_MAIN }} className="text-4xl font-bold mb-4">Loved by referees &amp; assigners</h2>
                <p style={{ color: TEXT_MUTED }} className="text-lg">Don&apos;t take our word for it.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {testimonials.map((t) => (
                    <div key={t.name} style={{ background: CARD_BG, border: "1px solid rgba(255,255,255,0.07)", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }} className="p-6 rounded-2xl flex flex-col">
                        <Stars n={t.rating} />
                        <p style={{ color: "#c8dae6" }} className="text-sm leading-relaxed flex-1 mb-5 italic">&ldquo;{t.quote}&rdquo;</p>
                        <div className="flex items-center gap-3">
                            <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                            <div>
                                <div style={{ color: TEXT_MAIN }} className="text-sm font-semibold">{t.name}</div>
                                <div style={{ color: TEXT_MUTED }} className="text-xs">{t.role}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const CTA = () => {
    const { handleNavigation } = useRouter();
    return (
        <section style={{ background: `linear-gradient(135deg, rgba(72,224,194,0.95) 0%, rgba(0,145,167,0.95) 100%)` }} className="py-20 px-4 sm:px-6">
            <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to modernize your referee program?</h2>
                <p className="text-base sm:text-lg text-white text-opacity-90 mb-8 opacity-90 max-w-2xl mx-auto">
                    Join hundreds of organizations already using RefAlign. Free to get started, no credit card required.
                </p>
                <button
                    onClick={() => handleNavigation("/signup")}
                    style={{ background: "#0d2230", color: TEXT_MAIN, border: `1px solid rgba(232,240,244,0.12)` }}
                    className="w-full sm:w-auto px-10 py-4 rounded-xl text-base font-bold hover:opacity-90 transition-all shadow-xl"
                >
                    Create your free account →
                </button>
            </div>
        </section>
    );
};

const Footer = () => {
    const { handleNavigation } = useRouter();
    return (
        <footer style={{ background: "#0a1220" }} className="py-12 px-4 sm:px-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between gap-8 mb-8">
                    <div className="max-w-xs">
                        <img src="/logo.svg" alt="RefAlign" className="h-8 mb-4" />
                        <p style={{ color: TEXT_MUTED }} className="text-sm leading-relaxed">
                            RefAlign is a referee scheduling and assignment platform redefining the way sports organizations manage their officials.
                        </p>
                    </div>
                    <div className="flex gap-16">
                        <div>
                            <h4 style={{ color: TEXT_MAIN }} className="text-sm font-semibold mb-3">Product</h4>
                            <div className="flex flex-col gap-2">
                                {[
                                    { label: "Pricing", path: "/pricing" },
                                    { label: "Blog", path: "/blog" },
                                    { label: "Get Started Free", path: "/signup" },
                                ].map((l) => <span key={l.label} onClick={() => handleNavigation(l.path)} style={{ color: TEXT_MUTED }} className="text-sm cursor-pointer hover:text-white transition-colors">{l.label}</span>)}
                            </div>
                        </div>
                        <div>
                            <h4 style={{ color: TEXT_MAIN }} className="text-sm font-semibold mb-3">Legal</h4>
                            <div className="flex flex-col gap-2">
                                <span style={{ color: TEXT_MUTED }} className="text-sm cursor-pointer hover:text-white transition-colors" onClick={() => handleNavigation("/privacypolicy")}>
                                    Privacy Policy
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }} className="pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
                    <p style={{ color: TEXT_MUTED }} className="text-xs">© 2026 RefAlign, Inc. All rights reserved.</p>
                    <p style={{ color: TEXT_MUTED }} className="text-xs">Built for sports organizations everywhere.</p>
                </div>
            </div>
        </footer>
    );
};

const LandingPage = () => (
    <div style={{ background: DARK_BG }} className="font-outfit overflow-x-hidden">
        <Nav />
        <main>
            <Hero />
            <Features />
            <HowItWorks />
            <Testimonials />
            <CTA />
        </main>
        <Footer />
    </div>
);

export default LandingPage;
